import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contestCandidateName, contestProjectTitle, type Contest } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { ContestMaturationPanel } from '@/features/concours/ContestMaturationPanel';
import { ContestDocumentsPanel } from '@/features/concours/ContestDocumentsPanel';
import { ContestRegistrationWizard } from '@/features/concours/ContestRegistrationWizard';
import { useToast } from '@/features/ui/toast/useToast';

export function ContestInscriptionPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [contests, setContests] = useState<Contest[]>([]);
  const [contestId, setContestId] = useState('');
  const [myRegs, setMyRegs] = useState<{ id: string; form_data: Record<string, string>; contest_id: string }[]>([]);
  const [openReg, setOpenReg] = useState<string | null>(null);

  async function loadMine() {
    if (!profile) return;
    const { data } = await supabase
      .from('contest_registrations')
      .select('id, form_data, contest_id')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    setMyRegs(data ?? []);
  }

  useEffect(() => {
    void supabase
      .from('contests')
      .select('*')
      .eq('status', 'launched')
      .then(({ data, error: err }) => {
        if (err) toast.error(err.message);
        else {
          const list = (data ?? []) as Contest[];
          setContests(list);
          if (list[0]) setContestId(list[0].id);
        }
      });
    void loadMine();
  }, [profile, toast]);

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Formulaire de candidature en 15 rubriques et suivi de vos dossiers.</p>
        </div>
        <div className="u-actions">
          <Link className="btn btn-ghost" to="/app/concours">
            Retour
          </Link>
          <Link className="btn btn-ghost" to="/app/concours/documentation">
            Documentation concours
          </Link>
          <Link className="btn btn-ghost" to="/app/concours/evolution">
            Suivre mon évolution
          </Link>
        </div>
      </div>

      <section className="u-card">
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          Formulaire d’inscription
        </h3>
        {contests.length === 0 ? (
          <p className="u-empty">Aucun concours lancé pour le moment.</p>
        ) : (
          <ContestRegistrationWizard
            contests={contests}
            contestId={contestId}
            onContestIdChange={setContestId}
            onSubmitted={(id) => {
              setOpenReg(id);
              void loadMine();
            }}
          />
        )}
      </section>

      {myRegs.length > 0 ? (
        <section className="u-card">
          <div className="u-card-head">
            <h3 className="u-section-title" style={{ marginTop: 0, marginBottom: 0 }}>
              Mes inscriptions
            </h3>
            <Link className="btn btn-ghost" to="/app/concours/evolution">
              Suivre mon évolution
            </Link>
          </div>
          <ul className="u-list">
            {myRegs.map((r) => (
              <li key={r.id} className="u-list-item">
                <div>
                  <strong>{contestProjectTitle(r.form_data) || r.id}</strong>
                  <span className="muted" style={{ display: 'block', fontSize: '0.9rem' }}>
                    {contestCandidateName(r.form_data)}
                  </span>
                </div>
                <button type="button" className="btn btn-ghost" onClick={() => setOpenReg(r.id)}>
                  Voir dossier
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {openReg ? (
        <section className="u-card">
          <h3 className="u-section-title" style={{ marginTop: 0 }}>
            Documents & maturation
          </h3>
          <p className="muted">Déposez les pièces demandées, puis consultez les décisions administrateur.</p>
          <ContestDocumentsPanel registrationId={openReg} />
          <div style={{ marginTop: '1.25rem' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>Parcours de maturation</h4>
            <ContestMaturationPanel registrationId={openReg} readOnly />
          </div>
        </section>
      ) : null}
    </div>
  );
}
